/**
 * @module      public/js/services/dialers/dialerindex.js           │  SERVICE LAYER
 * @summary     Provider-agnostic outbound-dialer factory.
 *
 * @description
 * Converts a **provider key** (`"twilio"`, `"zoom"`, `"system"`, …) into an
 * instantiated dialer that extends the abstract `BaseDialer`.
 *
 * Typical usage
 * -------------
 * ```js
 * // 1) in bootstrap/app.js
 * import { createDialer } from "../services/dialers/dialerindex.js";
 * di.dialer = createDialer(
 *   resolvedProvider,                 // e.g. "twilio"
 *   { apiClient: di.apiClient, endpoints: di.endpoints }
 * );
 *
 * // 2) anywhere else
 * const { dialer } = di;
 * await dialer.call({ to:"+15551231234", userId, contactId });
 * ```
 *
 * Named exports
 * -------------
 * – **createDialer(provider, deps?)** → `BaseDialer` instance
 * – **isDialer(obj)**                → run-time type-guard
 *
 * ## Depends On
 * – `./baseDialer.js`     — abstract interface
 * – `./twilioDialer.js`   — Twilio implementation (default)
 * – (future) `./vonageDialer.js`, etc.
 *
 * @author    Auto-Generated
 * @version   1.0.0
 * @since     2025-05-01
 * ------------------------------------------------------------------ */

import BaseDialer from './baseDialer.js';
import TwilioDialer from './twilioDialer.js';
import ZoomPhoneDialer from './zoomPhoneDialer.js';
import SystemPhoneDialer from './systemPhoneDialer.js';
// import VonageDialer from "./vonageDialer.js";   // ← add when ready

/** @type {Record<string, new (deps?:object) => BaseDialer>} */
const REGISTRY = {
  twilio: TwilioDialer,
  zoom: ZoomPhoneDialer,
  system: SystemPhoneDialer,
  // vonage : VonageDialer,
};

/* ───────────────────────── factory helpers ─────────────────────── */

/**
 * Create a new dialer for the given provider.
 *
 * @param   {string}  [provider="twilio"]  key in the registry
 * @param   {object}  [deps={}]            injected deps (apiClient, endpoints, etc.)
 * @returns {BaseDialer}
 */
export function createDialer(provider = 'twilio', deps = {}) {
  const DialerClass = REGISTRY[provider] || TwilioDialer;
  return new DialerClass(deps);
}

/**
 * Tiny run-time type-guard.
 *
 * @param   {any} obj
 * @returns {obj is BaseDialer}
 */
export function isDialer(obj) {
  return obj instanceof BaseDialer;
}
